import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Loader, RefreshCw, Smartphone } from "lucide-react";
import { API } from "../auth/AuthContext";
import { photoEntryFromApi } from "../lib/fileUrl";
import { formatInspectionSessionError } from "../lib/bookingErrors";
import { isMobileCameraDevice } from "../lib/cameraStream";

function withFullUrls(draft) {
  const out = {};
  for (const [angle, entry] of Object.entries(draft || {})) {
    const mapped = photoEntryFromApi(entry, angle);
    if (mapped) out[angle] = mapped;
  }
  return out;
}

/**
 * Shows a QR code so the user can continue inspection photo capture on their phone.
 * Polls the session and syncs draft photos back to the web form.
 */
export default function InspectionQrHandoff({
  phase,
  bookingId,
  vehicleId,
  spotId,
  parkingLabel,
  seats,
  active,
  photos,
  onPhotosSync,
  onSubmitted,
  onToken,
  onAnglesRequired,
  testid = "inspection-qr",
}) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pollStopped, setPollStopped] = useState(false);
  const [narrowViewport, setNarrowViewport] = useState(false);
  const lastJson = useRef("");
  const sessionGen = useRef(0);
  const pollCount = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setNarrowViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const startSession = useCallback(async (forceNew = false) => {
    const gen = ++sessionGen.current;
    setLoading(true);
    setError(null);
    try {
      const body = { phase };
      if (bookingId) body.booking_id = bookingId;
      if (vehicleId) body.vehicle_id = vehicleId;
      if (spotId) body.spot_id = spotId;
      if (parkingLabel) body.parking_label = parkingLabel;
      if (seats != null && seats !== "") body.seats = parseInt(seats, 10);
      const r = await axios.post(
        `${API}/inspections/qr-sessions`,
        body,
        { withCredentials: true, params: forceNew ? { force_new: true } : {} },
      );
      if (gen !== sessionGen.current) return;
      setSession(r.data);
      onToken?.(r.data.token);
      if (Array.isArray(r.data.angles_required)) onAnglesRequired?.(r.data.angles_required);
    } catch (e) {
      if (gen !== sessionGen.current) return;
      setError(formatInspectionSessionError(e.response?.data?.detail) || "Could not start phone session");
    } finally {
      if (gen === sessionGen.current) setLoading(false);
    }
  }, [phase, bookingId, vehicleId, spotId, parkingLabel, seats, onToken, onAnglesRequired]);

  useEffect(() => {
    if (!active) {
      setSession(null);
      setError(null);
      setPollStopped(false);
      pollCount.current = 0;
      return;
    }
    // Force a new session when seats change so angles_required stays in sync.
    const seatsChanged =
      session?.seats != null &&
      seats != null &&
      seats !== "" &&
      Number(session.seats) !== Number(seats);
    startSession(Boolean(seatsChanged));
    // intentionally omit session from deps — only react to active/startSession/seats
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, startSession, seats]);

  useEffect(() => {
    if (!session?.token || !active || pollStopped) return;
    pollCount.current = 0;

    const poll = async () => {
      if (document.hidden) return;
      if (pollCount.current >= 150) {
        setPollStopped(true);
        setError("Sync paused after 5 minutes — tap Refresh QR to reconnect.");
        return;
      }
      pollCount.current += 1;
      try {
        const r = await axios.get(`${API}/inspections/qr-sessions/${session.token}`);
        const data = r.data;
        if (data.status === "submitted") {
          onSubmitted?.(data);
          return;
        }
        if (data.status !== "active") {
          setError(formatInspectionSessionError(`session_not_active:can_refresh`));
          return;
        }
        setError(null);
        const merged = withFullUrls(data.draft_photos);
        const json = JSON.stringify(merged);
        if (json !== lastJson.current) {
          lastJson.current = json;
          onPhotosSync?.(merged);
        }
      } catch (e) {
        const msg = formatInspectionSessionError(e.response?.data?.detail);
        if (msg) setError(msg);
      }
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [session?.token, active, pollStopped, onPhotosSync, onSubmitted]);

  if (!active) return null;

  const doneCount = Object.keys(photos || {}).filter(k => photos[k]?.file_id).length;
  const total = session?.angles_required?.length || (phase === "parking_listing" ? 4 : 8);
  const sameDeviceCapture = isMobileCameraDevice() || narrowViewport;
  const captureUrl = session?.url || null;

  return (
    <div className="mb-5 p-4 rounded-xl border border-dashed border-[var(--zc-border)] bg-[var(--zc-bg-2)]" data-testid={testid}>
      {sameDeviceCapture && loading && (
        <div className="mb-4 w-full h-11 flex items-center justify-center gap-2 rounded-lg border border-[var(--zc-border)] bg-white text-sm text-[var(--zc-text-3)]">
          <Loader size={14} className="animate-spin" />
          Starting capture session…
        </div>
      )}
      {sameDeviceCapture && captureUrl && !loading && (
        <a
          href={captureUrl}
          className="mb-4 w-full h-11 zc-btn-primary text-sm flex items-center justify-center gap-2"
          data-testid={`${testid}-same-device`}
        >
          <Smartphone size={14} />
          Open capture on this phone
        </a>
      )}
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        {!sameDeviceCapture && (
          <div className="shrink-0 hidden sm:block">
            {loading && (
              <div className="w-[148px] h-[148px] flex items-center justify-center bg-white rounded-xl border border-[var(--zc-border)]">
                <Loader size={20} className="animate-spin text-[var(--zc-text-3)]" />
              </div>
            )}
            {!loading && captureUrl && (
              <div className="bg-white p-2.5 rounded-xl border border-[var(--zc-border)]" data-testid={`${testid}-code`}>
                <QRCodeSVG value={captureUrl} size={132} level="M" includeMargin={false} />
              </div>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Smartphone size={14} className="text-[var(--zc-accent)]" />
              {sameDeviceCapture ? "Capture on this device" : "Continue on your phone"}
            </div>
            <button
              type="button"
              onClick={() => { setPollStopped(false); pollCount.current = 0; startSession(true); }}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 h-8 zc-btn-ghost text-xs"
              data-testid={`${testid}-refresh`}
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              {sameDeviceCapture ? "Refresh session" : "Refresh QR"}
            </button>
          </div>
          <p className="text-xs text-[var(--zc-text-3)] mt-1.5 leading-relaxed">
            {sameDeviceCapture
              ? "Use the button above to open the camera flow on this device. Photos sync back here automatically."
              : "Scan with your phone camera. Photos sync here automatically. If you see a session error, tap Refresh QR."}
          </p>
          {session && !sameDeviceCapture && (
            <div className="zc-meta mt-2 font-mono truncate hidden sm:block" title={captureUrl}>
              {captureUrl}
            </div>
          )}
          <div className="text-xs text-[var(--zc-text-2)] mt-2">
            Phone progress: <span className="font-semibold">{doneCount}/{total}</span> {phase === "parking_listing" ? "photos" : "angles"}
          </div>
          {error && (
            <div className="text-xs text-[var(--zc-danger,#dc2626)] mt-2" data-testid={`${testid}-error`}>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
